import { StatePage } from "@/components/state-page";
import { formatCurrency, formatPercentage } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions } from "@/utils/permissions";
import { ActionIcon, Anchor, Button, Card, CopyButton, Skeleton, useModalsStack } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useCanGoBack, useRouter } from "@tanstack/react-router";
import type { AdminDiscountCode } from "@workspace/admin-domain/src/features/discount-code/schema";
import type { AdminTrip } from "@workspace/admin-domain/src/features/trip/schema";
import dayjs from "@workspace/domain/src/utils/dayjs";
import { t } from "i18next";
import {
  LucideArrowLeft,
  LucideCalendar,
  LucideChartArea,
  LucideEdit,
  LucideListOrdered,
  LucideMoreVertical,
  LucideRepeat,
  LucideTag,
  LucideTicket,
} from "lucide-react";
import * as z from "zod";
import Reservations from "../../-reservations/-index";
import { ReservationFiltersSchema } from "../../-reservations/-utils";
import { DEFAULT_TRIPS_PARAMS, TripFiltersSchema } from "../../trips/-utils";
import EditDiscountCode from "./-edit";
import DiscountCodeMenu from "./-menu";
import { DiscountCodeTrips, DiscountCodeTripsSkeleton } from "./-trips";

export const Route = createFileRoute("/_authenticated/discount-codes/$id/")({
  validateSearch: z.object({
    ...ReservationFiltersSchema.shape,
    ...TripFiltersSchema.shape,
  }),
  component: Component,
  beforeLoad: requirePermissions("discountCode:get"),
  loaderDeps: ({ search }) => search,
  loader: ({ context, params, deps }) => {
    void context.queryClient.prefetchQuery(
      orpc.discountCode.get.queryOptions({
        input: { id: params.id },
      }),
    );
    void context.queryClient.prefetchQuery(
      orpc.reservation.getAll.queryOptions({
        input: { value: params.id, type: "discountCodeId" },
      }),
    );
    void context.queryClient.prefetchInfiniteQuery(
      orpc.trip.getAll.infiniteOptions({
        input: (paginate) => ({
          ...DEFAULT_TRIPS_PARAMS,
          ...deps,
          discountCodeId: params.id,
          paginate,
        }),
        initialPageParam: undefined as AdminTrip["GetAll"]["paginate"],
        getNextPageParam: () => undefined as AdminTrip["GetAll"]["paginate"],
      }),
    );
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="discountCode" emptyResourceType="discountCode" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="discountCode" emptyResourceType="discountCode" />,
});

function Component() {
  const { id } = Route.useParams();
  const router = useRouter();
  const canGoBack = useCanGoBack();

  const stack = useModalsStack(["form", "confirm", "client", "force", "payment"]);

  const {
    data: discountCode,
    isLoading,
    error,
    refetch,
  } = useQuery(
    orpc.discountCode.get.queryOptions({
      input: { id },
    }),
  );

  if (isLoading) {
    return <DiscountCodeSkeleton />;
  }

  if (error) {
    return <StatePage error={error} reset={refetch} resourceType="discountCode" emptyResourceType="discountCode" />;
  }

  if (!discountCode) {
    return null;
  }

  return (
    <div className="gap-md flex flex-col">
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between">
        {canGoBack ? (
          <Anchor component="button" onClick={() => router.history.back()} className="flex items-center gap-1.5">
            <LucideArrowLeft className="size-6" />
          </Anchor>
        ) : null}

        <div className="flex items-center gap-1.5">
          <EditDiscountCode discountCode={discountCode} stack={stack} />
          <Button
            onClick={() => stack.open("form")}
            size="sm"
            variant="filled"
            leftSection={<LucideEdit className="size-4" />}
            disabled={CONSTANTS.DEMO}
          >
            {t("discountCodes.edit.edit")}
          </Button>

          <DiscountCodeMenu
            discountCode={discountCode}
            target={
              <ActionIcon size="lg" variant="default" className="size-9">
                <LucideMoreVertical className="size-4" />
              </ActionIcon>
            }
          />
        </div>
      </div>

      {/* Main layout - sidebar and content */}
      <div className="gap-md lg:gap-lg flex flex-col lg:flex-row">
        {/* Left sidebar with main client information */}
        <div className="gap-md flex w-full flex-col lg:w-92 lg:shrink-0">
          <DiscountCodeMainCard discountCode={discountCode} />
        </div>

        {/* Main content area - Statistics */}
        <div className="gap-md flex min-w-0 grow flex-col">
          <Card withBorder className="p-md min-w-0">
            <div className="gap-md flex flex-col">
              <div className="flex items-center gap-1.5">
                <div className="bg-indigo-light flex size-8 flex-none items-center justify-center rounded-full">
                  <LucideChartArea className="text-indigo-filled size-4" />
                </div>
                <h3 className="text-lg font-semibold">{t("clients.detail.statistics")}</h3>
              </div>

              <div
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(12.5rem, 1fr))",
                }}
                className="gap-md grid"
              >
                <div className="flex flex-col gap-1.5">
                  <div className="text-dimmed text-sm font-medium">{t("discountCodes.numberOfUses")}</div>
                  <div className="text-2xl font-bold">{discountCode.numberOfReservations}</div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="text-dimmed text-sm font-medium">{t("discountCodes.numberOfTrips")}</div>
                  <div className="text-2xl font-bold">{discountCode.numberOfTrips || t("discountCodes.allTrips")}</div>
                </div>
              </div>
            </div>
          </Card>

          <Reservations mode="discountCode" discountCode={discountCode} />
          {discountCode.numberOfTrips ? <DiscountCodeTrips discountCodeId={discountCode.code} /> : null}
        </div>
      </div>
    </div>
  );
}

function DiscountCodeMainCard({ discountCode }: { discountCode: AdminDiscountCode["DiscountCode"] }) {
  return (
    <Card withBorder className="overflow-hidden p-0">
      {/* Avatar Section */}
      <div className="z-10 -m-px flex">
        <div className="dark:bg-primary-800 bg-primary-500 relative h-32 w-full rounded-t-md">
          <div className="absolute inset-x-0 bottom-0 h-16 bg-linear-to-t from-black/20 via-black/10 to-transparent" />
          <div className="dark:bg-dark-500 absolute bottom-0 left-1/2 z-10 flex size-20 -translate-x-1/2 translate-y-1/2 items-center justify-center overflow-hidden rounded-full bg-gray-100">
            <LucideTicket className="text-dimmed size-10" />
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="gap-md p-md flex flex-col pt-12">
        <div className="text-center">
          <h1 className="text-xl font-semibold">{t("discountCodes.discountCode")}</h1>
        </div>

        <div className="gap-sm flex flex-col">
          <div className="gap-sm flex items-start">
            <LucideTag className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("discountCodes.code")}</div>

              <CopyButton value={discountCode.code}>
                {({ copied, copy }) => (
                  <Button
                    variant="subtle"
                    color={copied ? "green" : undefined}
                    size="compact-xs"
                    className="text-md -ms-1.5"
                    onClick={() => copy()}
                  >
                    {copied ? "Copied" : discountCode.code}
                  </Button>
                )}
              </CopyButton>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideTicket className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("discountCodes.type")}</div>
              <p className="font-medium">{t(`discountCodes.${discountCode.type}`)}</p>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideChartArea className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("discountCodes.value")}</div>
              <p className="font-medium">
                {discountCode.type === "PERCENTAGE"
                  ? formatPercentage(discountCode.value)
                  : formatCurrency(discountCode.value)}
              </p>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideRepeat className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("discountCodes.usageLimit")}</div>
              <p className="font-medium">
                {discountCode.usageLimit !== null ? discountCode.usageLimit : t("unlimited")}
              </p>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideListOrdered className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("discountCodes.newOnly")}</div>
              <p className="font-medium">{discountCode.newOnly ? t("yes") : t("no")}</p>
            </div>
          </div>

          <div className="gap-sm flex items-start">
            <LucideCalendar className="text-dimmed size-4 shrink-0" />
            <div className="flex flex-col gap-1.5">
              <div className="text-dimmed text-sm font-medium">{t("discountCodes.expiresAt")}</div>
              <p className="font-medium">
                {discountCode.expiresAt ? (
                  dayjs(discountCode.expiresAt).format("DD MMM YYYY")
                ) : (
                  <span className="text-dimmed">-</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function DiscountCodeSkeleton() {
  return (
    <div className="gap-md flex flex-col">
      {/* Header with breadcrumbs and actions */}
      <div className="flex items-center justify-between">
        <Skeleton className="size-6 rounded" />
        <div className="flex items-center gap-1.5">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="size-9 rounded" />
        </div>
      </div>

      {/* Main layout - sidebar and content */}
      <div className="gap-md lg:gap-lg flex flex-col lg:flex-row">
        {/* Left sidebar */}
        <div className="gap-md flex w-full flex-col lg:w-92 lg:shrink-0">
          <Card withBorder className="overflow-hidden p-0">
            <Skeleton className="h-32 w-full rounded-b-none" />
            <div className="gap-md p-md flex flex-col pt-12">
              <div className="text-center">
                <Skeleton className="mx-auto h-6 w-32" />
              </div>
              <div className="gap-sm mt-1.5 flex flex-col">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="gap-sm flex items-start">
                    <Skeleton className="size-4 shrink-0 rounded-full" />
                    <div className="flex grow flex-col gap-1.5">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Main content area */}
        <div className="gap-md flex min-w-0 grow flex-col">
          <Card withBorder className="p-md">
            <div className="mb-sm flex items-center gap-1.5">
              <Skeleton className="size-8 rounded-full" />
              <Skeleton className="h-5 w-20" />
            </div>

            <div className="gap-md grid md:grid-cols-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </div>
          </Card>

          {/* TODO?: why is this here? */}
          {/* <ReservationsSkeleton mode="client" showHeader={false} /> */}
          <DiscountCodeTripsSkeleton />
        </div>
      </div>
    </div>
  );
}
